import { Component, Input, OnInit } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { TimeOffPolicy } from '@app/models/time-off-v3/time-off-policy.model';
import { TimeOffRequestRules } from '@app/models/time-off-v3/time-off-request-rules.model';
import { TimeOffSeniorityOverride } from '@app/models/time-off-v3/time-off-seniority-override.model';
import { BaseForm } from '@app/modules/forms/base.form';
import { getTime, setTime } from '@time-off-v3/functions/time-unit-converter';
import {
    AccrualFrequencies,
    DECIMAL_PLACES,
    DECIMAL_PLACES_FOR_PIPE,
    END_OF_MONTH,
    MonthlyIntervals,
    MonthsForDropdown,
    TimeOffDateFormatDashes,
    TimeOffDateFormatDayMonthDashes,
} from '@time-off-v3/meta/time-off-meta';
import cloneDeep from 'lodash-es/cloneDeep';
import moment from 'moment';
import { AdvancedFormTooltips } from '../../meta/tooltip-meta';

@Component({
    selector: 'app-advanced-policy-form',
    templateUrl: './advanced-policy.form.html',
    styleUrls: ['./advanced-policy.form.scss'],
})
export class AdvancedPolicyForm extends BaseForm implements OnInit {
    @Input() timeOffPolicy: TimeOffPolicy;
    @Input() requestRules: TimeOffRequestRules;

    daysInMonth = MonthlyIntervals;
    months = MonthsForDropdown;
    timeOffSeniorityOverrides: TimeOffSeniorityOverride[] = [];
    timeOffSeniorityOverridesCache: TimeOffSeniorityOverride[] = [];
    timeOffPolicyCache: TimeOffPolicy;

    advancedFormTooltips = AdvancedFormTooltips;
    carryOverDay = 1;
    carryOverMonth = 0;
    pickADate = 'Pick a date';
    yearEnd = 'Year End';
    hired = 'Hired';
    decimalPlacesForPipe = DECIMAL_PLACES_FOR_PIPE;

    // Booleans for toggles
    showYearEndBalanceCap = false;
    showMaxBalanceCap = false;
    showNegativeBalanceCap = false;
    showWaitingPeriod = false;
    showLimitRequest = false;

    ngOnInit(): void {
        // Set initial values based on @input since the timeOffPolicy property will be bounded by this time.
        this.showYearEndBalanceCap = !!(this.timeOffPolicy.carryoverDate || this.timeOffPolicy.carriesOverOnHireDate);
        this.showMaxBalanceCap = this.timeOffPolicy.balanceCap === null ? false : !isNaN(this.timeOffPolicy.balanceCap);
        this.showNegativeBalanceCap = !!this.requestRules.negativeBalanceCap;
        this.showWaitingPeriod = !!this.requestRules.waitingPeriod;
        this.showLimitRequest = !!this.requestRules.minDayAmount || !!this.requestRules.maxLength;
        this.timeOffSeniorityOverrides = this.timeOffPolicy.timeOffSeniorityOverrides;
        // We cache the initial seniority overrides for delete comparision
        this.timeOffSeniorityOverridesCache = cloneDeep(this.timeOffSeniorityOverrides);
        this.timeOffPolicyCache = cloneDeep(this.timeOffPolicy);
    }

    addSeniorityOverride(): void {
        this.timeOffSeniorityOverrides.push(new TimeOffSeniorityOverride());
    }

    removeSeniorityOverride(index: number): void {
        this.timeOffSeniorityOverrides.splice(index, 1);
    }

    setCarryoverDate(carryoverDay: number): void {
        // If the value of the month is -1 then we get the last day
        this.timeOffPolicy.carryoverDate = moment()
            .month(this.carryOverMonth)
            .date(
                carryoverDay === END_OF_MONTH ? moment().month(this.carryOverMonth).endOf('month').date() : carryoverDay
            )
            .format(TimeOffDateFormatDashes);
    }

    setCarryoverMonth(carryoverMonth: number): void {
        // If the value of the month is -1 then we get the last day
        this.timeOffPolicy.carryoverDate = moment()
            .month(carryoverMonth)
            .date(
                this.carryOverDay === END_OF_MONTH
                    ? moment().month(this.carryOverMonth).endOf('month').date()
                    : this.carryOverDay
            )
            .format(TimeOffDateFormatDashes);
    }

    getCarryOverMonth(): number | void {
        if (this.timeOffPolicy.carryoverDate) {
            return moment(this.timeOffPolicy.carryoverDate, TimeOffDateFormatDashes).month();
        }
    }

    getAccruedValuePerPeriod(accruedValue: number): number {
        return getTime(accruedValue, this.timeOffPolicy?.timeOffType);
    }

    getCarryOverDay(): number | void {
        if (!this.timeOffPolicy.carryoverDate) {
            return;
        }

        const carryoverDate = moment(this.timeOffPolicy.carryoverDate);
        const isEndOfMonth = carryoverDate.isSame(moment().month(carryoverDate.month()).endOf('month'), 'day');

        if (isEndOfMonth) {
            return -1;
        }

        return moment(this.timeOffPolicy.carryoverDate, TimeOffDateFormatDashes).date();
    }

    setYearEndCapDate(): void {
        // Only want to run this if the user has selected 'Year End' as the option in the mat-select
        if (this.showYearEndBalanceCap) {
            this.timeOffPolicy.carryoverDate = moment().startOf('year').add(1, 'y').format(TimeOffDateFormatDashes);
        }
    }

    onCapOnDateSelectionChange(capOnDateSelection: MatSelect): void {
        switch (capOnDateSelection.value) {
            case this.yearEnd:
                this.timeOffPolicy.carriesOverOnHireDate = false;
                this.setYearEndCapDate();
                break;

            case this.hired:
                this.timeOffPolicy.carriesOverOnHireDate = true;
                this.timeOffPolicy.carryoverDate = null;
                break;

            case this.pickADate:
                this.timeOffPolicy.carriesOverOnHireDate = false;
                break;
        }
    }

    getCapOnDate(): string {
        if (!this.timeOffPolicy.carryoverDate && !this.timeOffPolicy.carriesOverOnHireDate) {
            return this.yearEnd;
        }

        if (!this.timeOffPolicy.carryoverDate && this.timeOffPolicy.carriesOverOnHireDate) {
            return this.hired;
        }
        // We don't need the requirement of checking the year for year end. Only care about checking if the first day of the year and month.
        if (
            moment(this.timeOffPolicy.carryoverDate).format(TimeOffDateFormatDayMonthDashes) ===
            moment().startOf('year').format(TimeOffDateFormatDayMonthDashes)
        ) {
            return this.yearEnd;
        }

        return this.pickADate;
    }

    resetCarryoverDateValues(): void {
        this.timeOffPolicy.carriesOverOnHireDate = false;
        this.timeOffPolicy.carryoverDate = null;
        this.timeOffPolicy.carryoverCap = null;
        this.timeOffPolicy.carryoverExpireAfter = null;
    }

    getDaysAccruedPerPeriod(seniorityOverride: TimeOffSeniorityOverride): number {
        // Prevent NaN
        if ([seniorityOverride.daysAccruedPerPeriod, this.timeOffPolicy.accrualFrequency].includes(undefined)) {
            return;
        }

        // If accrual frequency changes we need to recalculate the amount
        if (this.timeOffPolicy.id && this.timeOffPolicyCache.accrualFrequency !== this.timeOffPolicy.accrualFrequency) {
            const totalAmountPriorToChange =
                seniorityOverride.daysAccruedPerPeriod * AccrualFrequencies[this.timeOffPolicyCache.accrualFrequency];
            this.setDaysAccruedPerPeriod(seniorityOverride, totalAmountPriorToChange);
            this.timeOffPolicyCache = cloneDeep(this.timeOffPolicy);
        }

        const convertAccrualPerPeriodOnUnitOfTime = getTime(
            seniorityOverride.daysAccruedPerPeriod,
            this.timeOffPolicy.timeOffType
        );

        return +(convertAccrualPerPeriodOnUnitOfTime * AccrualFrequencies[this.timeOffPolicy.accrualFrequency]).toFixed(
            DECIMAL_PLACES
        );
    }

    setDaysAccruedPerPeriod(seniorityOverride: TimeOffSeniorityOverride, val: number): void {
        seniorityOverride.daysAccruedPerPeriod =
            setTime(val, this.timeOffPolicy.timeOffType) / AccrualFrequencies[this.timeOffPolicy.accrualFrequency];
    }
}
